import React from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import {
  FieldValues,
  FormContainer,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useGetGgpsLogin from "src/shared/data-access/ggpsLogin/useGetGgpsLogin";
import useReminderPass from "src/welcome/data-access/useReminderPass";
import { useGgpsNew } from "src/welcome/data-access/useGgpsNew";
import { OAuthModes } from "src/welcome/types";
import { ggpsLoginModes } from "src/namePurchase/data-access/ggpsLoginModes";

const schema = () => {
  const { t } = useTranslation();

  return yup.object().shape({
    username: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
    email: yup
      .string()
      .email(t("Μη έγκυρο email"))
      .required("Το πεδίο είναι υποχρεωτικό"),
  });
};

export default function Reminder() {
  const { t } = useTranslation();

  const [accountType, setAccountType] = React.useState("gemi");

  const validationSchema = schema();

  const formContext = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleAccountTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAccountType(event.target.value);
    formContext.reset();
  };

  const { mutate: gppLogin, isLoading: isLoadingGgpsLogin } = useGetGgpsLogin();
  const { mutate: mutateNew, isLoading: isLoadingGgpsLoginNew } =
    useGgpsNew();

  const { mutate: reminderPass, isLoading: isLoadingReminder } =
    useReminderPass();

  return (
    <Box
      sx={{
        width: "80vw",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Paper elevation={1} sx={{ p: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          {t("Τύπος Λογαριασμού")}
        </Typography>
        <RadioGroup
          aria-labelledby="account-type-radio-buttons-group-label"
          name="account-type-radio-buttons-group"
          value={accountType}
          onChange={handleAccountTypeChange}
        >
          <FormControlLabel
            value="nongemi"
            control={<Radio />}
            label={t("Δεν είμαι επιχείρηση υπόχρεη εγγραφής στο ΓΕΜΗ")}
          />
          <FormControlLabel
            value="gemi"
            control={<Radio />}
            label={t("Είμαι επιχείρηση υπόχρεη στο Γ.Ε.ΜΗ.")}
          />
        </RadioGroup>
      </Paper>

      <Paper elevation={1} sx={{ p: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          {t("Αρχικοποίηση Στοιχείων Πρόσβασης Ενδιαφερομένου")}
        </Typography>
        {accountType == "nongemi" ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                "Αν έχετε ξεχάσει το password σας μπορείτε με αυτή την οθόνη να το αρχικοποιήσετε ξανά και να σας αποσταλλεί στο e-mail σας. Για λόγους ασφάλειας όμως θα πρέπει να γίνει πρώτα διασταύρωση δίνοντας το username και το e-mail που δηλώσατε κατά την εγγραφή σας.",
              )}
            </Typography>
            <FormContainer
              formContext={formContext}
              onSuccess={(data: FieldValues) => {
                reminderPass({ ...data, accountType: accountType });
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "80%",
                  }}
                >
                  <Typography>{t("Username")}</Typography>
                  <TextFieldElement
                    name="username"
                    id="username"
                    placeholder="Username"
                    variant="outlined"
                    required
                    fullWidth
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "80%",
                  }}
                >
                  <Typography>{t("και e-mail Επικοινωνίας")}</Typography>
                  <TextFieldElement
                    name="email"
                    id="email"
                    required
                    placeholder="info@mycompany.gr"
                    variant="outlined"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <LoadingButton
                  variant="contained"
                  type="submit"
                  loading={isLoadingReminder}
                  disabled={
                    !formContext.watch("username") ||
                    !formContext.watch("email")
                  }
                >
                  {t(
                    "Να αρχικοποιηθεί και σταλεί νέος κωδικός πρόσβασης στο email μου",
                  )}
                </LoadingButton>
                <Link to="/">{t("Επιστροφή")}</Link>
              </Box>
            </FormContainer>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                "Αν έχετε ξεχάσει το password σας μπορείτε με αυτή την οθόνη να το αρχικοποιήσετε ξανά. Για λόγους ασφαλείας θα πρέπει να γίνει πρώτα διασταύρωση μέσω ΓΓΠΣ. Πιέστε το κουμπί για να μεταβείτε στη ΓΓΠΣ (Taxis). Θα σας ζητηθεί να δώσετε τα login / password σας ώστε να ληφθεί από τη ΓΓΠΣ το ΑΦΜ σας. Με το ΑΦΜ θα γίνει διασταύρωση στο Γ.Ε.ΜΗ. και στη συνέχεια θα πρέπει να δώσετε το νέο κωδικό για το παρόν σύστημα και θα συνδεθείτε αυτόματα.",
              )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <LoadingButton
                variant="contained"
                sx={{ textTransform: "none" }}
                loading={isLoadingGgpsLogin || isLoadingGgpsLoginNew}
                onClick={() => {
                  import.meta.env.VITE_OAUTH2_ACTIVATED === "true"
                    ? mutateNew({ mode: OAuthModes.LOGIN_REMINDER })
                    : gppLogin({ mode: ggpsLoginModes.REMINDER });
                }}
              >
                {t(
                  "Αρχικοποίηση Στοιχείων Πρόσβασης με Διασταύρωση ΑΦΜ μέσω ΓΓΠΣ",
                )}
              </LoadingButton>
              <Link to="/">{t("Επιστροφή")}</Link>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
